'use client'

import React, { useState, useCallback, ReactNode, useMemo, createContext, useContext } from 'react'
import { CSATSurvey } from './CSATSurvey'
import { useSurveysConfig, type AvailableSurveyIds, type SurveyTriggers } from '@/lib/csat/config'
import {
  setSurveyState,
  shouldShowSurvey,
  getActiveSurveyFromStorage,
  setActiveSurveyInStorage,
  clearActiveSurveyFromStorage,
  postponeSurvey,
} from '@/lib/csat/storage'
import type { SurveyConfig, CSATSurveyContextType } from '@/lib/csat/types'
import { datadogRum } from '@datadog/browser-rum'

interface CSATSurveyProviderProps {
  children?: ReactNode
}

const CSATSurveyContext = createContext<CSATSurveyContextType | null>(null)

type TriggerSurvey = <ID extends AvailableSurveyIds>(surveyId: ID, trigger: SurveyTriggers<ID>) => void

function CSATSurveyProvider({ children }: CSATSurveyProviderProps) {
  const [activeSurvey, setActiveSurvey] = useState<SurveyConfig['id'] | null>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      return getActiveSurveyFromStorage()
    }
    return null
  })

  const surveysConfig = useSurveysConfig()

  const triggerSurvey: TriggerSurvey = useCallback(
    (surveyId, trigger) => {
      async function triggerSurveyIfAllowed() {
        const surveyConfig = surveysConfig[surveyId]
        const shouldShow = await shouldShowSurvey(surveyConfig)

        if (shouldShow) {
          // Check if there's already an active survey
          if (activeSurvey && activeSurvey !== surveyId) {
            // Postpone this survey by 24 hours
            postponeSurvey(surveyId, 24)

            datadogRum.addAction('CSAT_SURVEY_POSTPONED', {
              SurveyID: surveyConfig.id,
              Question: surveyConfig.question,
              Trigger: trigger,
              Reason: 'collision_with_active_survey',
              ActiveSurvey: activeSurvey,
              PostponedHours: 24,
            })

            return
          }

          datadogRum.addAction('CSAT_SURVEY_TRIGGERED', {
            SurveyID: surveyConfig.id,
            Question: surveyConfig.question,
            Trigger: trigger,
          })

          setActiveSurvey(surveyId)
          setActiveSurveyInStorage(surveyId)
        }
      }

      triggerSurveyIfAllowed()
    },
    [surveysConfig, activeSurvey]
  )

  const deactivateSurvey = useCallback(
    (surveyId: SurveyConfig['id']) => {
      if (activeSurvey === surveyId) {
        setActiveSurvey(null)
        clearActiveSurveyFromStorage()
      }
    },
    [activeSurvey]
  )

  const value = useMemo(
    () => ({ triggerSurvey, activeSurveys: new Set(activeSurvey ? [activeSurvey] : []) }) as CSATSurveyContextType,
    [triggerSurvey, activeSurvey]
  )

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const promptsToRender = activeSurvey ? (
    <div className="fixed right-4 bottom-4 left-4 z-50">
      <CSATSurvey
        key={activeSurvey}
        question={surveysConfig[activeSurvey as AvailableSurveyIds].translated_question}
        feedbackBehavior={surveysConfig[activeSurvey as AvailableSurveyIds].feedbackBehavior}
        translateFollowUpQuestion={surveysConfig[activeSurvey as AvailableSurveyIds].translateFollowUpQuestion}
        onRatingClick={async ({ rating }) => {
          datadogRum.addAction('CSAT_SURVEY_SUBMITTED', {
            SurveyID: activeSurvey,
            Question: surveysConfig[activeSurvey as AvailableSurveyIds].question,
            Rating: rating,
          })

          try {
            await setSurveyState(activeSurvey, { submitted: true })
          } catch (error) {
            console.error('Error persisting CSAT survey', error)
          }

          // Check if feedback should be shown based on survey configuration
          const currentSurveyConfig = surveysConfig[activeSurvey as AvailableSurveyIds]
          const feedbackBehavior = currentSurveyConfig.feedbackBehavior?.showWhen ?? 'low_ratings'

          const shouldShowFeedback = (() => {
            switch (feedbackBehavior) {
              case 'always':
                return true
              case 'low_ratings':
                return rating <= 3
              case 'high_ratings':
                return rating > 3
              case 'high_and_low_ratings':
                return true
              case 'never':
                return false
              default:
                return rating <= 3
            }
          })()

          // Only auto-close if feedback shouldn't be shown
          if (!shouldShowFeedback && rating > 3) {
            await delay(3_000)
            deactivateSurvey(activeSurvey)
          }
        }}
        onFeedbackSubmit={async ({ rating, feedback }) => {
          datadogRum.addAction('CSAT_SURVEY_FEEDBACK_SUBMITTED', {
            SurveyID: activeSurvey,
            Question: surveysConfig[activeSurvey as AvailableSurveyIds].question,
            Rating: rating,
            Feedback: feedback,
          })

          await delay(3_000)
          deactivateSurvey(activeSurvey)
        }}
        onDismiss={async ({ rating }) => {
          datadogRum.addAction('CSAT_SURVEY_DISMISSED', {
            SurveyID: activeSurvey,
            Question: surveysConfig[activeSurvey as AvailableSurveyIds].question,
            Rating: rating,
          })

          if (rating === null) {
            try {
              await setSurveyState(activeSurvey, {
                dismissed: true,
                lastShownDate: new Date().toISOString(),
              })
            } catch (error) {
              console.error('Error persisting CSAT survey dismissal', error)
            }
          }

          deactivateSurvey(activeSurvey)
        }}
      />
    </div>
  ) : null

  return (
    <CSATSurveyContext.Provider value={value}>
      {children}
      {promptsToRender}
    </CSATSurveyContext.Provider>
  )
}

/**
 * Hook to trigger CSAT surveys imperatively.
 * Must be used within a CSATSurveyProvider.
 */
export function useCSATSurvey(): {
  triggerSurvey: TriggerSurvey
} {
  const context = useContext(CSATSurveyContext)

  if (!context) {
    throw new Error('useCSATSurvey must be used within a CSATSurveyProvider')
  }

  const { triggerSurvey } = context

  return useMemo(() => ({ triggerSurvey }), [triggerSurvey])
}

export { CSATSurveyProvider }
